import { fetchJSON as fetchAPI } from './client';

export type PhilosophyAction = {
  key: string;
  name: string;
  description: string;
};

export type LifecycleStage = {
  key: string;
  name: string;
  order: number;
};

export type OpportunityArea = {
  key: string;
  name: string;
  description?: string;
  sample_impact?: string;
  priority?: string;
};

export type RootCauseFactor = {
  key: string;
  name: string;
  category: string;
  probability?: number;
};

export type HypothesisTemplate = {
  required_fields: string[];
  example?: string;
};

export type RecommendationField = {
  key: string;
  name: string;
  description: string;
  required: boolean;
};

export type ConfidenceFactor = {
  key: string;
  name: string;
  weight: number;
  description?: string;
};

export type ExperimentSpecField = {
  key: string;
  name: string;
  description: string;
  required: boolean;
};

export type AttributionFactor = {
  key: string;
  name: string;
  contribution_pct?: number;
};

export type LearningCaptureItem = {
  key: string;
  name: string;
  description: string;
};

export type ExperimentLibraryEntry = {
  key: string;
  name: string;
  category: string;
  description: string;
  target_kpis: string[];
  extensible: boolean;
};

export type DecisionQuestion = {
  key: string;
  question: string;
  order: number;
};

export type RecommendationEvaluation = {
  recommendation_id: string;
  valid: boolean;
  has_measurable_intent: boolean;
  evidence_based: boolean;
  explainable: boolean;
  experiment_ready: boolean;
  gaps?: string[];
  evaluated_at: string;
};

export type GrowthBibleDashboard = {
  bible_id: string;
  tenant_id: string;
  version: string;
  depends_on: string[];
  lifecycle_stages: number;
  opportunity_areas: number;
  root_cause_factors: number;
  confidence_factors: number;
  experiment_library: number;
  decision_questions: number;
  active_experiments: number;
  open_recommendations: number;
  generated_at: string;
};

export type GrowthBibleStatus = {
  bible_id: string;
  version: string;
  depends_on: string[];
  lifecycle_stages: number;
  opportunity_areas: number;
  experiment_library: number;
  active_experiments: number;
  open_recommendations: number;
  scientific_engine: boolean;
  status: string;
};

const MARKETINTEL_BASE = import.meta.env.VITE_MARKETINTEL_API ?? '/api/v1/marketintel';

function fetchMarketIntel<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(`${MARKETINTEL_BASE}${path}`, init);
}

export const marketplaceGrowthExperimentBibleApi = {
  dashboard: () => fetchMarketIntel<GrowthBibleDashboard>('/growth-bible/dashboard'),
  philosophy: () => fetchMarketIntel<{ actions: PhilosophyAction[] }>('/growth-bible/philosophy').then((r) => r.actions),
  lifecycle: () => fetchMarketIntel<{ stages: LifecycleStage[] }>('/growth-bible/lifecycle').then((r) => r.stages),
  opportunities: () => fetchMarketIntel<{ opportunities: OpportunityArea[] }>('/growth-bible/opportunities').then((r) => r.opportunities),
  rootCauses: () => fetchMarketIntel<{ root_causes: RootCauseFactor[] }>('/growth-bible/root-causes').then((r) => r.root_causes),
  hypothesisTemplate: () => fetchMarketIntel<HypothesisTemplate>('/growth-bible/hypothesis-template'),
  recommendationSpec: () => fetchMarketIntel<{ fields: RecommendationField[] }>('/growth-bible/recommendation-spec').then((r) => r.fields),
  confidenceFactors: () => fetchMarketIntel<{ factors: ConfidenceFactor[] }>('/growth-bible/confidence-factors').then((r) => r.factors),
  experimentSpec: () => fetchMarketIntel<{ fields: ExperimentSpecField[] }>('/growth-bible/experiment-spec').then((r) => r.fields),
  attribution: () => fetchMarketIntel<{ factors: AttributionFactor[] }>('/growth-bible/attribution').then((r) => r.factors),
  learningCapture: () => fetchMarketIntel<{ items: LearningCaptureItem[] }>('/growth-bible/learning-capture').then((r) => r.items),
  experimentLibrary: () => fetchMarketIntel<{ experiments: ExperimentLibraryEntry[] }>('/growth-bible/experiment-library').then((r) => r.experiments),
  decisionQuestions: () => fetchMarketIntel<{ questions: DecisionQuestion[] }>('/growth-bible/decision-questions').then((r) => r.questions),
  evaluateRecommendation: (body: { recommendation_id: string; fields: Record<string, string> }) =>
    fetchMarketIntel<RecommendationEvaluation>('/growth-bible/evaluate-recommendation', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
  status: () => fetchMarketIntel<GrowthBibleStatus>('/growth-bible/status'),
};
